/**
 * @fileoverview Componente modal principal para selección de productos con vista de grid y detalle
 * @module ProductSelectionModal
 * @category Module - Organism
 * @since 1.0.0
 * @version 1.0.0
 */
import React, {
  useState, useMemo, useCallback, memo, useRef, useEffect
} from "react";
import { useTranslation } from 'react-i18next';
import useCategoriesApi from '../../../hooks/useCategoriesApi';
import ProductGrid from "./ProductSelectionModal/ProductGrid";
import ProductDetail from "./ProductSelectionModal/ProductDetail";
import CategoryFilter from "./ProductSelectionModal/CategoryFilter";
import ButtonComponent from "../../components/ButtonMC";
import IconMC from "../../components/IconMC";

/**
 * @typedef {Object} Product
 * @description Objeto producto con información completa
 * @property {string} id - Identificador único del producto
 * @property {string} name - Nombre del producto
 * @property {string} description - Descripción del producto
 * @property {number} price - Precio del producto
 * @property {Array} images - Array de URLs de imágenes
 * @property {string} category - Categoría del producto
 * @property {Object} metadata - Información adicional del producto
 */

/**
 * @typedef {Object} StepConfig
 * @description Configuración de botones y acciones para cada paso del flujo
 * @property {string} title - Título mostrado en el header del modal
 * @property {string|null} cancelLabel - Texto del botón cancelar
 * @property {string|null} submitLabel - Texto del botón enviar
 * @property {function|null} cancelAction - Función ejecutada al cancelar
 * @property {function|null} submitAction - Función ejecutada al enviar
 * @property {boolean} showSubmit - Mostrar o no el botón de envío
 */

/**
 * Componente para selección de productos con vista de grid y detalle.
 *
 * Implementa el patrón de contenido directo siguiendo el estilo de PaymentLinks,
 * sin modal wrapper, con posicionamiento absoluto y flujo de steps optimizado.
 *
 * @component ProductSelectionModal
 * @category Module - Organism
 *
 * @description
 * **Características:**
 * - Flujo de steps para selección de producto (ProductGrid → ProductDetail → CategoryFilter)
 * - Estado de producto seleccionado para navegación entre steps
 * - Configuración dinámica de botones según el step actual
 * - Sistema de navegación con botón de retroceso en detalle
 * - Contenido directo con posicionamiento absoluto (sin ModalMC)
 * - Integración con sharingInformationService para comunicación
 *
 * **Arquitectura (v1.0.0):**
 * - ✅ **Performance**: React.memo, useCallback, useMemo para optimización
 * - ✅ **State Management**: Estado local optimizado para producto seleccionado
 * - ✅ **Step Navigation**: Sistema de navegación entre grid, detalle y filtros
 * - ✅ **Dynamic Config**: Configuración de botones según contexto
 * - ✅ **Service Integration**: Comunicación vía sharingInformationService
 * - ✅ **Direct Content**: Patrón de contenido directo como PaymentLinks
 *
 * @param {Object} props - Propiedades del componente
 * @param {function} props.onClose - Función para cerrar el componente
 * @param {function} [props.onProductSelect] - Callback al seleccionar producto
 * @param {string} [props.className] - Clases CSS adicionales
 *
 * @returns {JSX.Element} Elemento JSX renderizado con contenido de selección de productos
 *
 * @example
 * ```jsx
 * // Uso básico del componente de productos
 * <ProductSelectionModal 
 *   onClose={handleCloseModal}
 *   onProductSelect={handleProductSelected}
 *   className="tw-custom-positioning"
 * />
 * ```
 *
 * @see {@link ProductGrid} Componente de cuadrícula de productos
 * @see {@link ProductDetail} Componente de detalle de producto
 * @see {@link CategoryFilter} Componente de filtros de categorías
 *
 * @since 1.0.0
 * @version 1.0.0 - Implementación inicial con patrón de contenido directo
 */
const ProductSelectionModal = memo(({
  onClose,
  onProductSelect,
  className
}) => {
  const { t } = useTranslation('Products');

  /**
   * Estado del step actual en el flujo de selección de producto.
   *
   * @description
   * **Steps Disponibles:**
   * 1. ProductGrid - Vista de cuadrícula de productos
   * 2. ProductDetail - Vista de detalle de producto seleccionado
   * 3. CategoryFilter - Vista de filtros de categorías
   *
   * **Inicialización:**
   * - Siempre inicia en step 1 (ProductGrid)
   * - Avanza a step 2 cuando se selecciona un producto
   * - Avanza a step 3 cuando se abren filtros
   *
   * @type {number}
   */
  const [step, setStep] = useState(1);

  /**
   * Estado del producto seleccionado actualmente.
   *
   * @description
   * **Uso:**
   * - null en step 1 (ProductGrid)
   * - Objeto Product en step 2 (ProductDetail)
   * - Se establece cuando usuario selecciona producto en grid
   * - Se limpia al retroceder al grid
   *
   * @type {Product|null}
   */
  const [selectedProduct, setSelectedProduct] = useState(null);

  /**
   * Estado de las categorías seleccionadas para filtrar productos.
   *
   * @description
   * **Uso:**
   * - Array de objetos de categorías seleccionadas
   * - Se pasa a ProductGrid para filtrar productos
   * - Se actualiza desde CategoryFilter (step 3)
   * - Se puede limpiar desde CategoryFilter
   *
   * @type {Array<Object>}
   */
  const [selectedCategories, setSelectedCategories] = useState([]);

  /**
   * Estado del producto seleccionado para envío con sus variantes.
   *
   * @description
   * **Uso:**
   * - Mantiene el producto con variantes seleccionadas
   * - Se actualiza cuando se seleccionan variantes en ProductDetail
   * - Se usa en las acciones del footer para envío
   *
   * @type {Product|null}
   */
  const [selectedProductToSend, setSelectedProductToSend] = useState(null);

  const [search, setSearch] = useState('');

  /**
   * Referencia al componente CategoryFilter para acceder a su estado local.
   *
   * @description
   * **Uso:**
   * - Permite acceder a localSelectedIds sin causar re-renders
   * - Se usa en handleSaveCategoryFilters y handleClearCategoryFilters
   * - Evita warnings de React sobre actualizaciones durante render
   *
   * @type {React.RefObject}
   */
  const categoryFilterRef = useRef();

  /**
   * Hook de categorías API para obtener la lista completa de categorías.
   */
  const {
    categories,
    fetchCategories
  } = useCategoriesApi();

  /**
   * Efecto para cargar categorías al montar el componente.
   */
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  /**
   * Función para avanzar al siguiente step en el flujo.
   *
   * Permite navegar hacia adelante en el proceso de selección,
   * específicamente desde ProductGrid a ProductDetail.
   *
   * @function nextStep
   * @memberof ProductSelectionModal
   * @private
   *
   * @description
   * **Comportamiento:**
   * - Incrementa step en 1
   * - Utilizada cuando usuario selecciona producto en grid
   * - Memoizada para optimizar re-renders
   *
   * @returns {void} No retorna valor, actualiza estado interno
   *
   * @example
   * ```javascript
   * // Avanzar al detalle del producto
   * nextStep();
   * ```
   */
  const nextStep = useCallback(() => {
    setStep((prevStep) => prevStep + 1);
  }, []);

  /**
   * Función para retroceder al step anterior en el flujo.
   *
   * Permite navegación hacia atrás, específicamente desde
   * ProductDetail de vuelta a ProductGrid, o desde CategoryFilter a ProductGrid.
   *
   * @function prevStep
   * @memberof ProductSelectionModal
   * @private
   *
   * @description
   * **Comportamiento:**
   * - Decrementa step en 1
   * - Limpia producto seleccionado si retrocede desde ProductDetail
   * - Utilizada por botón "Atrás" en ProductDetail y CategoryFilter
   *
   * @returns {void} No retorna valor, actualiza estado interno
   *
   * @example
   * ```javascript
   * // Volver al grid de productos
   * prevStep();
   * ```
   */
  const prevStep = useCallback(() => {
    setStep((pStep) => pStep - 1);
    if (step === 2) {
      setSelectedProduct(null);
    }
  }, [step]);

  /**
   * Función para manejar la selección de un producto.
   *
   * Se ejecuta cuando el usuario hace clic en un producto
   * en ProductGrid para ver sus detalles.
   *
   * @function handleProductSelect
   * @memberof ProductSelectionModal
   * @private
   *
   * @description
   * **Comportamiento:**
   * - Establece el producto seleccionado
   * - Avanza al step 2 (ProductDetail)
   * - Memoizada para optimizar performance
   *
   * @param {Product} product - Producto seleccionado por el usuario
   *
   * @returns {void} No retorna valor, actualiza estado interno
   *
   * @example
   * ```javascript
   * // Seleccionar producto y ver detalle
   * handleProductSelect(productData);
   * ```
   */
  const handleProductSelect = useCallback((product) => {
    setSelectedProduct(product);
    nextStep();
  }, [nextStep]);

  /**
   * Función para abrir el filtro de categorías.
   *
   * Se ejecuta cuando el usuario hace clic en el icono de filtros
   * en ProductGrid para configurar filtros de categorías.
   *
   * @function handleOpenCategoryFilter
   * @memberof ProductSelectionModal
   * @private
   *
   * @description
   * **Comportamiento:**
   * - Cambia al step 3 (CategoryFilter)
   * - Conserva el estado de filtros actuales
   * - Permite configurar nuevos filtros
   *
   * @returns {void} No retorna valor, actualiza estado interno
   *
   * @example
   * ```javascript
   * // Abrir filtros de categorías
   * handleOpenCategoryFilter();
   * ```
   */
  const handleOpenCategoryFilter = useCallback(() => {
    setStep(3);
  }, []);

  /**
   * Función para cerrar el componente de selección.
   *
   * Cierra el componente y puede limpiar el estado interno.
   *
   * @function closeModal
   * @memberof ProductSelectionModal
   * @private
   *
   * @description
   * **Comportamiento:**
   * - Ejecuta callback onClose prop
   * - Resetea estado interno del componente
   *
   * @returns {void} No retorna valor, realiza cleanup
   *
   * @example
   * ```javascript
   * // Cerrar componente de productos
   * closeModal();
   * ```
   */
  const closeModal = useCallback(() => {
    // Resetear estado al cerrar
    setStep(1);
    setSelectedProduct(null);
    setSelectedCategories([]);
    setSelectedProductToSend(null);
    setSearch('');

    onClose?.();
  }, [onClose]);

  /**
   * Maneja el guardado de filtros de categorías.
   */
  const handleSaveCategoryFilters = useCallback(() => {
    const localCategoryIds = categoryFilterRef.current?.getLocalSelectedIds() || [];

    // Convertir IDs a objetos completos de categorías
    const selectedCategoryObjects = localCategoryIds
      .map((id) => categories.find((cat) => cat.web_id === id))
      .filter(Boolean); // Remover valores undefined

    setSelectedCategories(selectedCategoryObjects);
    setStep(1); // Volver al grid de productos
  }, [categories]);

  /**
   * Maneja el limpiado de filtros de categorías.
   */
  const handleClearCategoryFilters = useCallback(() => {
    setSelectedCategories([]);
    categoryFilterRef.current?.clearLocalSelectedIds();
    setStep(1); // Volver al grid de productos
  }, []);

  /**
   * Maneja el envío del producto seleccionado.
   */
  const handleSendProduct = useCallback(() => {
    if (selectedProductToSend) {
      onProductSelect?.({
        selectProduct: true,
        selectedProduct: selectedProductToSend,
        sendCollection: false
      });
    }
  }, [
    selectedProductToSend,
    onProductSelect
  ]);

  /**
   * Maneja el envío de la colección (galería).
   */
  const handleSendCollection = useCallback(() => {
    if (selectedProductToSend) {
      onProductSelect?.({
        selectProduct: true,
        selectedProduct: selectedProductToSend,
        sendCollection: true
      });
    }
  }, [
    selectedProductToSend,
    onProductSelect
  ]);

  /**
   * Configuración dinámica según el step actual.
   *
   * @description
   * Determina el título y comportamiento de navegación según el step actual.
   *
   * @type {Object}
   */
  const stepConfig = useMemo(() => {
    switch (step) {
      case 1:
        return {
          title: t('productGrid.title'),
          showBackButton: false
        };
      case 2:
        return {
          title: t('productDetail.title'),
          showBackButton: true
        };
      case 3:
        return {
          title: t('categoryFilter.title'),
          showBackButton: true
        };
      default:
        return {
          title: '',
          showBackButton: false
        };
    }
  }, [step, t]);

  /**
   * Contenido según el step actual.
   *
   * @description
   * **Renderización Condicional:**
   * - Step 1: Renderiza ProductGrid con handler de selección
   * - Step 2: Renderiza ProductDetail con producto seleccionado
   * - Step 3: Renderiza CategoryFilter con filtros
   * - Default: Renderiza null
   *
   * @type {JSX.Element}
   */
  const stepContent = useMemo(() => {
    return (
      <>
        <div className={step === 1 ? 'tw-h-full tw-flex tw-flex-col' : 'tw-hidden'}>
          <ProductGrid
            columns={2}
            onProductSelect={handleProductSelect}
            onOpenCategoryFilter={handleOpenCategoryFilter}
            selectedCategories={selectedCategories}
            searchValue={search}
            onSearchChange={setSearch}
          />
        </div>
        {step === 2 && (
          <ProductDetail
            product={selectedProduct}
            selectedProductToSend={selectedProductToSend}
            setSelectedProductToSend={setSelectedProductToSend}
            onBack={prevStep}
            onSendCollection={handleSendCollection}
          />
        )}
        {step === 3 && (
          <CategoryFilter
            ref={categoryFilterRef}
            selectedCategoryIds={selectedCategories.map((cat) => cat.web_id)}
          />
        )}
      </>
    );
  }, [
    step,
    selectedProduct,
    selectedProductToSend,
    selectedCategories,
    search,
    handleProductSelect,
    handleOpenCategoryFilter,
    prevStep,
    handleSendCollection
  ]);

  /**
   * Footer personalizado con botones de acción para Step 2 y 3.
   */
  const footerContent = useMemo(() => {
    if (step === 2 && selectedProductToSend) {
      const hasMultipleImages = selectedProductToSend
        && !selectedProductToSend?.is_combination
        && selectedProductToSend?.image_urls?.length > 1;

      return (
        <div className="tw-flex tw-gap-2 tw-w-full">
          {hasMultipleImages && (
            <ButtonComponent
              variant="GRAY"
              text={t('productDetail.sendGallery')}
              onClick={handleSendCollection}
              className="tw-flex-1"
            />
          )}
          <ButtonComponent
            text={t('productDetail.sendProduct')}
            onClick={handleSendProduct}
            className="tw-flex-1"
          />
        </div>
      );
    }

    if (step === 3) {
      return (
        <div className="tw-flex tw-flex-col tw-gap-2">
          <ButtonComponent
            text={t('categoryFilter.save')}
            onClick={handleSaveCategoryFilters}
            className="tw-w-full"
          />
          <ButtonComponent
            variant="TEXT_PLAIN"
            text={t('categoryFilter.clear')}
            onClick={handleClearCategoryFilters}
            className="tw-w-full tw-text-blue-500 hover:tw-text-blue-600 active:tw-text-blue-700"
          />
        </div>
      );
    }

    return null;
  }, [
    step,
    selectedProductToSend,
    t,
    handleSendProduct,
    handleSendCollection,
    handleClearCategoryFilters,
    handleSaveCategoryFilters
  ]);

  /**
   * Header personalizado con título y navegación.
   */
  const header = useMemo(() => (
    <div className="tw-flex tw-justify-between tw-items-center tw-p-1 tw-border-b tw-border-gray-200">
      <div className="tw-flex tw-items-center tw-gap-3">
        {stepConfig.showBackButton && (
          <IconMC
            name="arrow-ios-back-outline"
            onClick={prevStep}
            className="tw-cursor-pointer tw-text-gray-500 hover:tw-text-gray-700"
          />
        )}
        <span className="tw-font-semibold tw-text-gray-950 tw-text-sm">
          {stepConfig.title}
        </span>
      </div>
      <IconMC
        name="close-outline"
        onClick={closeModal}
        className="tw-cursor-pointer tw-text-gray-500 hover:tw-text-gray-700"
      />
    </div>
  ), [stepConfig, prevStep, closeModal]);

  const mainContainerClassName = useMemo(() => `tw-flex tw-flex-col tw-gap-2 ${className || ''}`, [className]);

  return (
    <div className={mainContainerClassName}>
      {header}
      <div className="tw-flex-1 tw-overflow-y-auto tw-p-2">
        {stepContent}
      </div>
      {footerContent && (
        <div className="tw-p-4 tw-border-t tw-border-gray-200">
          {footerContent}
        </div>
      )}
    </div>
  );
});

/**
 * Nombre de display para debugging y herramientas de desarrollo.
 *
 * @type {string}
 */
ProductSelectionModal.displayName = 'ProductSelectionModal';

export default ProductSelectionModal;